// src/App.tsx — 75 Hard Tracker

import React, { useState, useEffect, useCallback, Component } from 'react';
import { AppState, Attempt, Challenge, DayLog, PRESET_CHALLENGES } from './types';
import { loadState, saveState, defaultState, savePhoto, getPhoto, getAllPhotos } from './storage';
import {
  todayStr, getDayNumber, isDayComplete, getTotalCompletedDays,
  getCompletionPercentage, generateId, getDateForDay, formatDate,
  formatDateShort, getStreak, getLongestStreak, getRandomQuote,
  getTaskCompletionToday, getGreeting, getMilestone, formatWeekday,
  isFutureDate
} from './utils';
import { addDays, parseISO, format } from 'date-fns';
import './App.css';

type View = 'onboarding' | 'home' | 'today' | 'calendar' | 'progress' | 'photos' | 'challenges' | 'settings' | 'new-challenge';

function App() {
  const [state, setState] = useState<AppState>(defaultState);
  const [view, setView] = useState<View>('home');
  const [loaded, setLoaded] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [todayPhoto, setTodayPhoto] = useState<string | null>(null);
  const [photoTimeline, setPhotoTimeline] = useState<{ date: string; dataUri: string }[]>([]);
  const [installPrompt, setInstallPrompt] = useState<any>(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [pendingChallenge, setPendingChallenge] = useState<Challenge | null>(null);
  const [customStartDate, setCustomStartDate] = useState(todayStr());

  useEffect(() => {
    loadState().then(s => {
      // Check for premium unlock from Stripe redirect
      const params = new URLSearchParams(window.location.search);
      if (params.get('premium') === 'unlocked') {
        s = { ...s, premium: true };
        saveState(s);
        window.history.replaceState({}, '', window.location.pathname);
      }
      setState(s);
      if (!s.onboarded) setView('onboarding');
      setLoaded(true);
    });
  }, []);

  // PWA install prompt
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e);
      // Show banner after user has been in the app for a bit
      setTimeout(() => setShowInstallBanner(true), 30000);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const save = useCallback(async (newState: AppState) => {
    setState(newState);
    await saveState(newState);
  }, []);

  async function handleUpgrade() {
    try {
      const res = await fetch('/api/checkout', { method: 'POST' });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch {
      alert('Something went wrong. Please try again.');
    }
  }

  const activeAttempt = state.attempts.find(a => a.id === state.activeAttemptId) || null;
  const activeChallenge = activeAttempt
    ? state.challenges.find(c => c.id === activeAttempt.challengeId) || null
    : null;

  useEffect(() => {
    if (activeAttempt) {
      getPhoto(activeAttempt.id, selectedDate || todayStr()).then(setTodayPhoto);
    }
  }, [activeAttempt, view, selectedDate]);

  if (!loaded) {
    return (
      <div className="app loading-screen">
        <div className="loading-icon">🔥</div>
      </div>
    );
  }

  // ─── Core actions ─────────────────────────────────────────────

  function startChallenge(challenge: Challenge, startDate?: string) {
    const attempt: Attempt = {
      id: generateId(),
      challengeId: challenge.id,
      startDate: startDate || todayStr(),
      currentDay: 1,
      status: 'active',
      logs: {},
    };
    save({ ...state, attempts: [...state.attempts, attempt], activeAttemptId: attempt.id });
    setPendingChallenge(null);
    setView('today');
  }

  function toggleTask(taskId: string, dateStr?: string) {
    if (!activeAttempt || !activeChallenge) return;
    const date = dateStr || todayStr();
    const log: DayLog = activeAttempt.logs[date] || { date, tasks: {} };
    const newTasks = { ...log.tasks, [taskId]: !log.tasks[taskId] };
    const newLog: DayLog = { ...log, tasks: newTasks };

    const allDone = activeChallenge.tasks.filter(t => t.required).every(t => newTasks[t.id]);
    if (allDone && !newLog.completedAt) {
      newLog.completedAt = new Date().toISOString();
    } else if (!allDone) {
      newLog.completedAt = undefined;
    }

    const newAttempt: Attempt = { ...activeAttempt, logs: { ...activeAttempt.logs, [date]: newLog } };

    if (getTotalCompletedDays(newAttempt, activeChallenge) >= activeChallenge.durationDays) {
      newAttempt.status = 'completed';
      newAttempt.completedDate = todayStr();
    }

    save({ ...state, attempts: state.attempts.map(a => a.id === newAttempt.id ? newAttempt : a) });
  }

  function saveNotes(notes: string, dateStr?: string) {
    if (!activeAttempt) return;
    const date = dateStr || todayStr();
    const log: DayLog = activeAttempt.logs[date] || { date, tasks: {} };
    const newAttempt: Attempt = { ...activeAttempt, logs: { ...activeAttempt.logs, [date]: { ...log, notes } } };
    save({ ...state, attempts: state.attempts.map(a => a.id === newAttempt.id ? newAttempt : a) });
  }

  function failAttempt() {
    if (!activeAttempt) return;
    const updated: Attempt = { ...activeAttempt, status: 'failed', failedOnDay: getDayNumber(activeAttempt), failedDate: todayStr() };
    save({ ...state, attempts: state.attempts.map(a => a.id === updated.id ? updated : a), activeAttemptId: null });
    setView('home');
  }

  function saveCustomChallenge(challenge: Challenge) {
    save({ ...state, challenges: [...state.challenges, challenge] });
    setView('challenges');
  }

  function resizeImage(file: File, maxWidth: number): Promise<string> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let w = img.width, h = img.height;
          if (w > maxWidth) { h = (h * maxWidth) / w; w = maxWidth; }
          canvas.width = w;
          canvas.height = h;
          canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL('image/jpeg', 0.8));
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    });
  }

  async function handlePhotoCapture(e: React.ChangeEvent<HTMLInputElement>, dateStr?: string) {
    if (!activeAttempt || !e.target.files?.[0]) return;
    const date = dateStr || todayStr();
    const dataUri = await resizeImage(e.target.files[0], 800);
    await savePhoto(activeAttempt.id, date, dataUri);
    setTodayPhoto(dataUri);
  }

  // ─── LANDING PAGE / ONBOARDING ─────────────────────────────────

  function renderOnboarding() {
    return (
      <div className="view landing-view">
        {/* Hero */}
        <div className="landing-hero">
          <div className="landing-logo">🔥</div>
          <h1 className="landing-title">75 Hard<br/><span className="landing-title-sub">Tracker</span></h1>
          <p className="landing-tagline">The simplest way to track your<br/>75 Hard challenge. Free. Private. Clean.</p>
          <button className="btn btn-primary btn-large btn-glow" onClick={() => { save({ ...state, onboarded: true }); setView('home'); }}>
            Start Your Challenge →
          </button>
          <p className="landing-no-signup">No sign up. No credit card. No BS.</p>
        </div>

        {/* Social proof */}
        <div className="landing-section">
          <div className="landing-proof">
            <div className="proof-stat">
              <span className="proof-number">75</span>
              <span className="proof-label">Days of discipline</span>
            </div>
            <div className="proof-stat">
              <span className="proof-number">6</span>
              <span className="proof-label">Daily tasks</span>
            </div>
            <div className="proof-stat">
              <span className="proof-number">0</span>
              <span className="proof-label">Excuses allowed</span>
            </div>
          </div>
        </div>

        {/* What is 75 Hard */}
        <div className="landing-section">
          <h2 className="landing-h2">What is 75 Hard?</h2>
          <p className="landing-body">75 Hard is a mental toughness program. Every day for 75 days, you complete all the tasks — no exceptions, no substitutions. Miss one? You start over from Day 1.</p>
          <div className="landing-tasks">
            {[
              { emoji: '💪', text: 'Two 45-minute workouts (one must be outdoors)' },
              { emoji: '🥗', text: 'Follow a diet — any diet, just stick to it' },
              { emoji: '💧', text: 'Drink one gallon (3.8L) of water' },
              { emoji: '📖', text: 'Read 10 pages of a non-fiction book' },
              { emoji: '📸', text: 'Take a progress photo' },
            ].map(t => (
              <div key={t.text} className="landing-task">
                <span>{t.emoji}</span>
                <span>{t.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Features */}
        <div className="landing-section">
          <h2 className="landing-h2">Why use this app?</h2>
          <div className="landing-features">
            {[
              { icon: '✅', title: 'Daily check-ins', desc: 'Tap to mark each task done. See your progress instantly.' },
              { icon: '📸', title: 'Progress photos', desc: 'Capture daily photos and watch your transformation over time.' },
              { icon: '🔥', title: 'Streak tracking', desc: 'See your current streak, best streak, and completion rate.' },
              { icon: '📅', title: 'Calendar view', desc: 'Your entire 75 days at a glance. Tap any day to review.' },
              { icon: '🎯', title: 'Multiple challenges', desc: '75 Hard, 75 Soft, 30 Day Kickstart, or create your own.' },
              { icon: '📊', title: 'Stats & milestones', desc: 'Per-task breakdown, motivational milestones, and more.' },
            ].map(f => (
              <div key={f.title} className="landing-feature-card">
                <span className="landing-feature-icon">{f.icon}</span>
                <div>
                  <strong>{f.title}</strong>
                  <p>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Challenges */}
        <div className="landing-section">
          <h2 className="landing-h2">Not ready for 75 Hard?</h2>
          <p className="landing-body">We've got easier starting points built in — or create your own custom challenge with any tasks, any duration.</p>
          <div className="landing-challenge-pills">
            <span className="l-pill" style={{ borderColor: '#f9731644', color: '#f97316' }}>🔥 75 Hard</span>
            <span className="l-pill" style={{ borderColor: '#8b5cf644', color: '#8b5cf6' }}>💜 75 Medium</span>
            <span className="l-pill" style={{ borderColor: '#06b6d444', color: '#06b6d4' }}>🩵 75 Soft</span>
            <span className="l-pill" style={{ borderColor: '#22c55e44', color: '#22c55e' }}>💚 30 Day Kickstart</span>
            <span className="l-pill" style={{ borderColor: '#52525244', color: '#a3a3a3' }}>✨ Custom Challenge</span>
          </div>
        </div>

        {/* Privacy */}
        <div className="landing-section">
          <div className="landing-privacy-card">
            <span className="landing-privacy-icon">🔒</span>
            <h3>Your data stays on your device</h3>
            <p>No accounts. No cloud. No tracking. No ads. Everything is stored locally on your phone. We literally can't see your data — and that's by design.</p>
          </div>
        </div>

        {/* CTA */}
        <div className="landing-section landing-cta">
          <h2 className="landing-h2">Ready to start?</h2>
          <p className="landing-body">75 days. That's all it takes to prove to yourself that you can do hard things.</p>
          <button className="btn btn-primary btn-large btn-glow" onClick={() => { save({ ...state, onboarded: true }); setView('home'); }}>
            Start Your Challenge →
          </button>
          <p className="landing-no-signup">Free forever. Works on any phone.</p>
        </div>

        {/* Footer */}
        <div className="landing-footer">
          <p>the75app.com</p>
        </div>
      </div>
    );
  }

  // ─── HOME / DASHBOARD ────────────────────────────────────────

  function renderHome() {
    if (!activeAttempt || !activeChallenge) {
      return (
        <div className="view home-view">
          <div className="hero">
            <div className="hero-emoji">🔥</div>
            <h1>75 Hard Tracker</h1>
            <p className="subtitle">Choose a challenge and commit.</p>
          </div>
          <div className="section">
            <h2>Challenges</h2>
            <div className="challenge-list">
              {state.challenges.map(c => (
                <button key={c.id} className="challenge-card" onClick={() => { setPendingChallenge(c); setCustomStartDate(todayStr()); }}
                  style={{ borderColor: c.color ? `${c.color}33` : undefined }}>
                  <div className="challenge-card-header">
                    <span className="challenge-name">{c.name}</span>
                    <span className="challenge-days" style={{
                      background: c.color ? `${c.color}20` : undefined,
                      color: c.color || undefined
                    }}>{c.durationDays} days</span>
                  </div>
                  <p className="challenge-desc">{c.description}</p>
                  <div className="challenge-tasks">
                    {c.tasks.map(t => <span key={t.id} className="task-pill">{t.emoji} {t.label}</span>)}
                  </div>
                  <div className="challenge-start-hint">Tap to start →</div>
                </button>
              ))}
            </div>
            
            {/* Start date picker modal */}
            {pendingChallenge && (
              <div className="modal-overlay" onClick={() => setPendingChallenge(null)}>
                <div className="modal-card" onClick={e => e.stopPropagation()}>
                  <h3>Start {pendingChallenge.name}</h3>
                  <p className="modal-desc">When did you start (or want to start)?</p>
                  <div className="form-group" style={{ marginBottom: 16 }}>
                    <label>Start date</label>
                    <input type="date" value={customStartDate} onChange={e => setCustomStartDate(e.target.value)}
                      max={todayStr()} className="date-input" />
                  </div>
                  <button className="btn btn-primary" style={{ background: pendingChallenge.color || '#f97316' }}
                    onClick={() => startChallenge(pendingChallenge, customStartDate)}>
                    Start Challenge →
                  </button>
                  <button className="btn btn-ghost btn-small" onClick={() => setPendingChallenge(null)}>Cancel</button>
                </div>
              </div>
            )}
            {state.premium ? (
              <button className="btn btn-ghost" onClick={() => setView('new-challenge')}>+ Create Custom Challenge</button>
            ) : (
              <button className="btn btn-ghost premium-locked" onClick={handleUpgrade}>🔒 Custom Challenges — Unlock Premium ($4.99)</button>
            )}
          </div>
          {state.attempts.filter(a => a.status !== 'active').length > 0 && (
            <div className="section">
              <h2>History</h2>
              {state.attempts.filter(a => a.status !== 'active').slice().reverse().map(a => {
                const ch = state.challenges.find(c => c.id === a.challengeId);
                if (!ch) return null;
                return (
                  <div key={a.id} className={`attempt-card attempt-${a.status}`}>
                    <div className="attempt-header">
                      <span>{ch.name}</span>
                      <span className={`status-badge status-${a.status}`}>
                        {a.status === 'completed' ? '✅ Completed' : `❌ Day ${a.failedOnDay}`}
                      </span>
                    </div>
                    <p className="attempt-meta">{formatDate(a.startDate)} · {getTotalCompletedDays(a, ch)} days completed</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    const today = todayStr();
    const dayNum = Math.min(getDayNumber(activeAttempt, today), activeChallenge.durationDays);
    const pct = getCompletionPercentage(activeAttempt, activeChallenge);
    const todayLog = activeAttempt.logs[today];
    const todayComplete = isDayComplete(todayLog, activeChallenge.tasks);
    const completedDays = getTotalCompletedDays(activeAttempt, activeChallenge);
    const streak = getStreak(activeAttempt, activeChallenge);
    const { done, total } = getTaskCompletionToday(todayLog, activeChallenge.tasks);
    const milestone = getMilestone(dayNum, activeChallenge.durationDays);
    const accentColor = activeChallenge.color || '#f97316';

    return (
      <div className="view home-view">
        <div className="dashboard-header">
          <p className="greeting">{getGreeting()}</p>
          <h1 className="challenge-title-small">{activeChallenge.name}</h1>
        </div>
        <div className="dashboard-card main-card">
          <div className="day-display">
            <span className="day-label">DAY</span>
            <span className="day-number" style={{ color: accentColor }}>{dayNum}</span>
            <span className="day-total">/ {activeChallenge.durationDays}</span>
          </div>
          <div className="progress-ring-container">
            <svg className="progress-ring" viewBox="0 0 120 120">
              <circle className="progress-ring-bg" cx="60" cy="60" r="52" />
              <circle className="progress-ring-fill" cx="60" cy="60" r="52"
                style={{ stroke: accentColor }}
                strokeDasharray={`${pct * 3.267} ${326.7 - pct * 3.267}`}
                strokeDashoffset="81.675" />
            </svg>
            <div className="progress-ring-text"><span className="pct">{pct}%</span></div>
          </div>
          {milestone && <div className="milestone-banner"><p>{milestone}</p></div>}
        </div>
        <div className="stats-grid">
          <div className="stat-card"><span className="stat-value">{completedDays}</span><span className="stat-label">Days Done</span></div>
          <div className="stat-card"><span className="stat-value">{activeChallenge.durationDays - completedDays}</span><span className="stat-label">Remaining</span></div>
          <div className="stat-card"><span className="stat-value">{streak}🔥</span><span className="stat-label">Streak</span></div>
          <div className="stat-card"><span className="stat-value">{done}/{total}</span><span className="stat-label">Today</span></div>
        </div>
        {todayComplete ? (
          <div className="today-complete-card">
            <span className="check-big">✅</span>
            <h3>Day {dayNum} complete!</h3>
            <p className="quote">"{getRandomQuote()}"</p>
          </div>
        ) : (
          <button className="btn btn-primary btn-large" onClick={() => setView('today')} style={{ background: accentColor }}>
            {done > 0 ? `Continue check-in (${done}/${total})` : "Start today's check-in"} →
          </button>
        )}
        <div className="section">
          <button className="btn btn-danger-ghost btn-small" onClick={() => {
            if (window.confirm('End this attempt? It will be marked as failed and saved in your history.')) failAttempt();
          }}>I missed a day — restart</button>
        </div>
      </div>
    );
  }

  // ─── TODAY CHECK-IN ───────────────────────────────────────────

  function renderToday() {
    if (!activeAttempt || !activeChallenge) {
      return (
        <div className="view empty-view">
          <div className="empty-icon">✅</div>
          <h2>No active challenge</h2>
          <p>Start a challenge to begin checking in.</p>
          <button className="btn btn-primary" onClick={() => setView('home')}>Choose a Challenge</button>
        </div>
      );
    }

    const date = selectedDate || todayStr();
    const dayNum = getDayNumber(activeAttempt, date);
    const log = activeAttempt.logs[date] || { date, tasks: {} };
    const allDone = isDayComplete(log, activeChallenge.tasks);
    const future = isFutureDate(date);

    return (
      <div className="view today-view">
        <div className="today-header">
          <div className="today-header-top">
            <div>
              <h1>Day {dayNum}</h1>
              <p className="today-date">{formatWeekday(date)}, {formatDate(date)}</p>
            </div>
            {allDone && <span className="header-check">✅</span>}
          </div>
          {selectedDate && selectedDate !== todayStr() && (
            <button className="btn btn-ghost btn-small" onClick={() => setSelectedDate(null)}>← Back to today</button>
          )}
        </div>

        {future ? (
          <div className="future-day"><p>This day hasn't arrived yet. Stay patient. 🧘</p></div>
        ) : (
          <>
            <div className="task-list">
              {activeChallenge.tasks.map(task => (
                <button key={task.id} className={`task-item ${log.tasks[task.id] ? 'task-done' : ''}`}
                  onClick={() => toggleTask(task.id, date)}>
                  <span className="task-check">
                    {log.tasks[task.id] ? (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <rect width="24" height="24" rx="6" fill={activeChallenge.color || '#f97316'} />
                        <path d="M7 12.5L10.5 16L17 9" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <rect x="1" y="1" width="22" height="22" rx="5" stroke="#404040" strokeWidth="2" />
                      </svg>
                    )}
                  </span>
                  <span className="task-emoji">{task.emoji}</span>
                  <span className="task-label">{task.label}</span>
                </button>
              ))}
            </div>
            <div className="section">
              <h2>📸 Progress Photo</h2>
              {todayPhoto ? (
                <div className="photo-preview">
                  <img src={todayPhoto} alt="Progress" />
                  <label className="btn btn-ghost btn-small photo-retake">
                    Retake
                    <input type="file" accept="image/*" capture="environment" onChange={(e) => handlePhotoCapture(e, date)} hidden />
                  </label>
                </div>
              ) : (
                <label className="photo-capture-btn">
                  <span className="photo-icon">📷</span>
                  <span>Tap to take progress photo</span>
                  <input type="file" accept="image/*" capture="environment" onChange={(e) => handlePhotoCapture(e, date)} hidden />
                </label>
              )}
            </div>
            <div className="section">
              <h2>📝 Notes</h2>
              <textarea className="notes-input" placeholder="How did today go? Any wins?"
                value={log.notes || ''} onChange={(e) => saveNotes(e.target.value, date)} rows={3} />
            </div>
            {allDone && (
              <div className="day-complete-banner">
                <div className="share-card-content">
                  <span className="fire-emoji">🔥</span>
                  <h2 style={{ color: activeChallenge.color || '#f97316' }}>Day {dayNum} Complete!</h2>
                  <p>{activeChallenge.name} · {getCompletionPercentage(activeAttempt, activeChallenge)}%</p>
                  <div className="share-tasks">
                    {activeChallenge.tasks.map(t => <span key={t.id}>✅ {t.emoji}</span>)}
                  </div>
                  <p className="share-hint">Screenshot this to share 📱</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // ─── CALENDAR ─────────────────────────────────────────────────

  function renderCalendar() {
    if (!activeAttempt || !activeChallenge) {
      return (
        <div className="view empty-view">
          <div className="empty-icon">📅</div>
          <h2>No active challenge</h2>
          <button className="btn btn-primary" onClick={() => setView('home')}>Choose a Challenge</button>
        </div>
      );
    }

    const startDate = parseISO(activeAttempt.startDate);
    const endDate = addDays(startDate, activeChallenge.durationDays - 1);
    const today = todayStr();

    const days = Array.from({ length: activeChallenge.durationDays }, (_, i) => {
      const d = addDays(startDate, i);
      const dateStr = format(d, 'yyyy-MM-dd');
      const log = activeAttempt.logs[dateStr];
      const complete = isDayComplete(log, activeChallenge.tasks);
      const isT = dateStr === today;
      const future = isFutureDate(dateStr);
      const status = complete ? 'complete' : isT ? 'today' : future ? 'future' : 'missed';
      return { date: dateStr, dayNum: i + 1, status };
    });

    return (
      <div className="view calendar-view">
        <h1>Calendar</h1>
        <p className="subtitle">{formatDateShort(activeAttempt.startDate)} → {formatDateShort(format(endDate, 'yyyy-MM-dd'))}</p>
        <div className="calendar-stats">
          <span className="cal-stat">✅ {getTotalCompletedDays(activeAttempt, activeChallenge)} done</span>
          <span className="cal-stat">🔥 {getLongestStreak(activeAttempt, activeChallenge)} best streak</span>
        </div>
        <div className="calendar-grid">
          {days.map(d => (
            <button key={d.date} className={`cal-day cal-${d.status}`}
              onClick={() => { if (d.status !== 'future') { setSelectedDate(d.date); setView('today'); } }}
              style={d.status === 'complete' ? { borderColor: `${activeChallenge.color || '#22c55e'}44`, background: `${activeChallenge.color || '#22c55e'}15` } : undefined}>
              <span className="cal-num">{d.dayNum}</span>
              <span className="cal-icon">{d.status === 'complete' ? '✅' : d.status === 'missed' ? '❌' : d.status === 'today' ? '📍' : '·'}</span>
            </button>
          ))}
        </div>
        <div className="calendar-legend">
          <span>✅ Done</span><span>❌ Missed</span><span>📍 Today</span>
        </div>
      </div>
    );
  }

  // ─── PROGRESS ─────────────────────────────────────────────────

  function renderProgress() {
    if (!activeAttempt || !activeChallenge) {
      return (
        <div className="view empty-view">
          <div className="empty-icon">📊</div>
          <h2>No active challenge</h2>
          <button className="btn btn-primary" onClick={() => setView('home')}>Choose a Challenge</button>
        </div>
      );
    }

    const completedDays = getTotalCompletedDays(activeAttempt, activeChallenge);
    const pct = getCompletionPercentage(activeAttempt, activeChallenge);
    const streak = getStreak(activeAttempt, activeChallenge);
    const longestStreak = getLongestStreak(activeAttempt, activeChallenge);
    const dayNum = getDayNumber(activeAttempt);
    const attemptNum = state.attempts.filter(a => a.challengeId === activeChallenge.id).length;
    const accentColor = activeChallenge.color || '#f97316';

    const taskStats = activeChallenge.tasks.map(task => {
      const completed = Object.values(activeAttempt.logs).filter(log => log.tasks[task.id]).length;
      return { ...task, completed, rate: Math.round((completed / Math.max(dayNum, 1)) * 100) };
    });

    return (
      <div className="view progress-view">
        <h1>Progress</h1>
        <div className="progress-hero">
          <span className="big-number" style={{ color: accentColor }}>{completedDays}</span>
          <span className="big-label">days completed</span>
        </div>
        <div className="progress-bar-container">
          <div className="progress-bar" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${accentColor}, ${accentColor}cc)` }} />
        </div>
        <p className="progress-pct">{pct}% of {activeChallenge.durationDays} days</p>
        <div className="stats-grid progress-stats">
          <div className="stat-card"><span className="stat-value">{streak}🔥</span><span className="stat-label">Current Streak</span></div>
          <div className="stat-card"><span className="stat-value">{longestStreak}⭐</span><span className="stat-label">Best Streak</span></div>
          <div className="stat-card"><span className="stat-value">#{attemptNum}</span><span className="stat-label">Attempt</span></div>
          <div className="stat-card"><span className="stat-value">{activeChallenge.durationDays - completedDays}</span><span className="stat-label">Remaining</span></div>
        </div>
        <div className="section">
          <h2>Task Completion</h2>
          {state.premium ? (
            taskStats.map(t => (
              <div key={t.id} className="task-stat">
                <div className="task-stat-header">
                  <span>{t.emoji} {t.label}</span>
                  <span className="task-stat-pct">{t.rate}%</span>
                </div>
                <div className="progress-bar-container small">
                  <div className="progress-bar" style={{ width: `${t.rate}%`, background: accentColor }} />
                </div>
              </div>
            ))
          ) : (
            <div className="premium-teaser">
              <p className="muted">See detailed per-task completion rates</p>
              <button className="btn btn-ghost premium-locked" onClick={handleUpgrade}>🔒 Unlock Premium ($4.99)</button>
            </div>
          )}
        </div>
        {state.premium ? (
          <button className="btn btn-ghost" onClick={async () => {
            const photos = await getAllPhotos(activeAttempt.id);
            setPhotoTimeline(photos);
            setView('photos');
          }}>📸 View Photo Timeline</button>
        ) : (
          <button className="btn btn-ghost premium-locked" onClick={handleUpgrade}>🔒 Photo Timeline — Unlock Premium ($4.99)</button>
        )}
      </div>
    );
  }

  // ─── PHOTO TIMELINE ───────────────────────────────────────────

  function renderPhotos() {
    return (
      <div className="view photos-view">
        <button className="btn btn-ghost btn-small" onClick={() => setView('progress')}>← Back</button>
        <h1>Progress Photos</h1>
        {photoTimeline.length === 0 ? (
          <div className="empty-view inline">
            <div className="empty-icon">📸</div>
            <h2>No photos yet</h2>
            <p>Take progress photos during your daily check-ins.</p>
          </div>
        ) : (
          <div className="photo-grid">
            {photoTimeline.map(p => (
              <div key={p.date} className="photo-card">
                <img src={p.dataUri} alt={`Progress ${p.date}`} />
                <span className="photo-date">{formatDateShort(p.date)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ─── SETTINGS ─────────────────────────────────────────────────

  function renderSettings() {
    const isInstalled = window.matchMedia('(display-mode: standalone)').matches;

    return (
      <div className="view settings-view">
        <h1>Settings</h1>

        {!isInstalled && (
          <div className="section">
            <h2>Install App</h2>
            {installPrompt ? (
              <button className="btn btn-primary" onClick={async () => {
                installPrompt.prompt();
                const result = await installPrompt.userChoice;
                if (result.outcome === 'accepted') {
                  setInstallPrompt(null);
                  setShowInstallBanner(false);
                }
              }}>📲 Add to Home Screen</button>
            ) : (
              <div className="install-instructions">
                <p className="muted">To install this app on your phone:</p>
                <p className="muted"><strong>iPhone:</strong> Tap the share button ⬆️ then "Add to Home Screen"</p>
                <p className="muted"><strong>Android:</strong> Tap the menu ⋮ then "Add to Home Screen"</p>
              </div>
            )}
          </div>
        )}
        <div className="section">
          <h2>Challenges</h2>
          <button className="btn btn-ghost" onClick={() => setView('challenges')}>Manage Challenges</button>
        </div>
        <div className="section">
          <h2>Data</h2>
          {state.premium ? (
            <button className="btn btn-ghost" onClick={async () => {
              const data = JSON.stringify(state, null, 2);
              const blob = new Blob([data], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url; a.download = `75hard-backup-${todayStr()}.json`; a.click();
              URL.revokeObjectURL(url);
            }}>📥 Export Backup</button>
          ) : (
            <button className="btn btn-ghost premium-locked" onClick={handleUpgrade}>🔒 Export Backup — Unlock Premium ($4.99)</button>
          )}
          <button className="btn btn-danger-ghost btn-small" style={{ marginTop: 12 }} onClick={() => {
            if (window.confirm('Delete ALL data? This cannot be undone.')) {
              save(defaultState);
              setView('home');
            }
          }}>🗑️ Delete All Data</button>
        </div>
        <div className="section about-section">
          {state.premium ? (
            <p className="premium-badge-inline">⭐ Premium Unlocked</p>
          ) : (
            <button className="btn btn-primary btn-large" onClick={handleUpgrade} style={{ marginBottom: 12 }}>
              ⭐ Unlock Premium — $4.99
            </button>
          )}
          <p className="muted">75 Hard Tracker v1.0</p>
          <p className="muted">All data stored locally on your device.</p>
          <p className="muted">No accounts. No tracking. No cloud.</p>
        </div>
      </div>
    );
  }

  // ─── CHALLENGES LIBRARY ───────────────────────────────────────

  function renderChallenges() {
    return (
      <div className="view challenges-view">
        <h1>Challenges</h1>
        <div className="challenge-list">
          {state.challenges.map(c => (
            <div key={c.id} className="challenge-card static" style={{ borderColor: c.color ? `${c.color}33` : undefined }}>
              <div className="challenge-card-header">
                <span className="challenge-name">{c.name}</span>
                <span className="challenge-days" style={{ background: c.color ? `${c.color}20` : undefined, color: c.color || undefined }}>{c.durationDays} days</span>
              </div>
              <p className="challenge-desc">{c.description}</p>
              <div className="challenge-tasks">
                {c.tasks.map(t => <span key={t.id} className="task-pill">{t.emoji} {t.label}</span>)}
              </div>
              {c.isCustom && (
                <button className="btn btn-danger-ghost btn-small" style={{ marginTop: 8 }} onClick={() => {
                  if (window.confirm(`Delete "${c.name}"?`)) {
                    save({ ...state, challenges: state.challenges.filter(ch => ch.id !== c.id) });
                  }
                }}>Delete</button>
              )}
            </div>
          ))}
        </div>
        <button className="btn btn-ghost" onClick={() => setView('new-challenge')}>+ Create Custom Challenge</button>
        <button className="btn btn-ghost btn-small" style={{ marginTop: 8 }} onClick={() => setView('settings')}>← Back</button>
      </div>
    );
  }

  // ─── NEW CHALLENGE FORM ───────────────────────────────────────

  function renderNewChallenge() {
    return <NewChallengeForm onSave={saveCustomChallenge} onCancel={() => setView(activeAttempt ? 'settings' : 'home')} />;
  }

  // ─── RENDER ───────────────────────────────────────────────────

  const views: Record<View, () => React.ReactNode> = {
    onboarding: renderOnboarding,
    home: renderHome,
    today: renderToday,
    calendar: renderCalendar,
    progress: renderProgress,
    photos: renderPhotos,
    challenges: renderChallenges,
    settings: renderSettings,
    'new-challenge': renderNewChallenge,
  };

  return (
    <div className="app" data-theme={state.settings.theme}>
      <main className="main-content">
        {views[view]()}
      </main>
      {view !== 'onboarding' && showInstallBanner && installPrompt && (
        <InstallBanner installPrompt={installPrompt} onDismiss={() => setShowInstallBanner(false)} />
      )}
      {view !== 'onboarding' && (
        <nav className="bottom-nav">
          <button className={`nav-btn ${view === 'home' ? 'active' : ''}`}
            onClick={() => { setSelectedDate(null); setView('home'); }}>
            <span className="nav-icon">🏠</span><span className="nav-label">Home</span>
          </button>
          <button className={`nav-btn ${view === 'today' ? 'active' : ''}`}
            onClick={() => { setSelectedDate(null); setView('today'); }}>
            <span className="nav-icon">✅</span><span className="nav-label">Today</span>
          </button>
          <button className={`nav-btn ${view === 'calendar' ? 'active' : ''}`}
            onClick={() => setView('calendar')}>
            <span className="nav-icon">📅</span><span className="nav-label">Calendar</span>
          </button>
          <button className={`nav-btn ${view === 'progress' ? 'active' : ''}`}
            onClick={() => setView('progress')}>
            <span className="nav-icon">📊</span><span className="nav-label">Stats</span>
          </button>
          <button className={`nav-btn ${view === 'settings' ? 'active' : ''}`}
            onClick={() => setView('settings')}>
            <span className="nav-icon">⚙️</span><span className="nav-label">More</span>
          </button>
        </nav>
      )}
    </div>
  );
}

// ─── Install Banner ─────────────────────────────────────────────

function InstallBanner({ installPrompt, onDismiss }: { installPrompt: any; onDismiss: () => void }) {
  if (!installPrompt) return null;
  return (
    <div className="install-banner">
      <div className="install-banner-content">
        <span className="install-banner-icon">📲</span>
        <div className="install-banner-text">
          <strong>Install 75 Hard Tracker</strong>
          <p>Add to your home screen for the best experience</p>
        </div>
      </div>
      <div className="install-banner-actions">
        <button className="btn btn-primary btn-small" onClick={async () => {
          installPrompt.prompt();
          const result = await installPrompt.userChoice;
          onDismiss();
        }}>Install</button>
        <button className="btn-icon" onClick={onDismiss}>✕</button>
      </div>
    </div>
  );
}

// ─── New Challenge Form ─────────────────────────────────────────

function NewChallengeForm({ onSave, onCancel }: { onSave: (c: Challenge) => void; onCancel: () => void }) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [days, setDays] = useState(75);
  const [color, setColor] = useState('#f97316');
  const [tasks, setTasks] = useState([{ id: generateId(), label: '', emoji: '✅', required: true as boolean }]);

  const COLORS = ['#f97316', '#8b5cf6', '#06b6d4', '#22c55e', '#ec4899', '#eab308', '#ef4444'];

  function addTask() {
    setTasks([...tasks, { id: generateId(), label: '', emoji: '✅', required: true }]);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || tasks.some(t => !t.label.trim())) return;
    onSave({
      id: generateId(),
      name: name.trim(),
      description: desc.trim(),
      durationDays: days,
      tasks: tasks.map(t => ({ ...t, label: t.label.trim() })),
      isCustom: true,
      color,
    });
  }

  return (
    <div className="view new-challenge-view">
      <h1>Create Challenge</h1>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Name</label>
          <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="My Challenge" />
        </div>
        <div className="form-group">
          <label>Description</label>
          <textarea value={desc} onChange={e => setDesc(e.target.value)} placeholder="What's this challenge about?" />
        </div>
        <div className="form-group">
          <label>Duration (days)</label>
          <input type="number" value={days} onChange={e => setDays(parseInt(e.target.value) || 1)} min={1} max={365} />
        </div>
        <div className="form-group">
          <label>Color</label>
          <div className="color-picker">
            {COLORS.map(c => (
              <button key={c} type="button" className={`color-dot ${color === c ? 'selected' : ''}`}
                style={{ background: c }} onClick={() => setColor(c)} />
            ))}
          </div>
        </div>
        <div className="form-group">
          <label>Daily Tasks</label>
          {tasks.map((task, i) => (
            <div key={task.id} className="task-edit-row">
              <input type="text" className="emoji-input" value={task.emoji}
                onChange={e => setTasks(tasks.map(t => t.id === task.id ? { ...t, emoji: e.target.value } : t))} maxLength={4} />
              <input type="text" value={task.label}
                onChange={e => setTasks(tasks.map(t => t.id === task.id ? { ...t, label: e.target.value } : t))}
                placeholder={`Task ${i + 1}`} className="task-label-input" />
              {tasks.length > 1 && (
                <button type="button" className="btn-icon" onClick={() => setTasks(tasks.filter(t => t.id !== task.id))}>×</button>
              )}
            </div>
          ))}
          <button type="button" className="btn btn-ghost btn-small" onClick={addTask}>+ Add Task</button>
        </div>
        <div className="form-actions">
          <button type="submit" className="btn btn-primary" style={{ background: color }}>Create Challenge</button>
          <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>
        </div>
      </form>
    </div>
  );
}

// ─── Error Boundary ─────────────────────────────────────────────

class ErrorBoundary extends Component<{ children: React.ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ background: '#0a0a0a', color: '#fafafa', padding: 40, minHeight: '100vh', fontFamily: 'system-ui' }}>
          <h1 style={{ fontSize: 24, marginBottom: 16 }}>Something went wrong</h1>
          <p style={{ color: '#a3a3a3', marginBottom: 16 }}>Try clearing your browser data for this site and reloading.</p>
          <button onClick={() => {
            localStorage.clear();
            if ('indexedDB' in window) {
              indexedDB.deleteDatabase('75HardTracker');
            }
            window.location.reload();
          }} style={{ background: '#f97316', color: 'white', border: 'none', padding: '12px 24px', borderRadius: 8, fontSize: 16, cursor: 'pointer' }}>
            Reset & Reload
          </button>
          <pre style={{ marginTop: 20, fontSize: 12, color: '#525252', whiteSpace: 'pre-wrap' }}>{this.state.error.message}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

function AppWithErrorBoundary() {
  return <ErrorBoundary><App /></ErrorBoundary>;
}

export default AppWithErrorBoundary;